/**
 * R지수 band boundaries for the 환자 상세 헤더 R칩 popover (SHCT-675).
 *
 * PRD 04 §3.1 requires the popover to PRINT the three grade ranges, and both PRD 04 §3.1 and PRD 11 §7.2
 * end that same sentence with 「※ 경계값은 어드민에서 관리」. Until this module the popover printed the
 * numbers as literal i18n copy, so an administrator editing 데이터기준값 > 위험지수(R) changed the bands
 * the server classified by while the popover kept reciting the values it shipped with.
 *
 * medical-api already treats this exact class of number as server-owned: the home dashboard serves the
 * 고위험 boundary rather than letting the client hold it, "which would keep printing the old boundary
 * after an edit while the list beside it already used the new one". This is the same rule applied to the
 * one place still doing it.
 *
 * The fallback below is NOT a second opinion about where the boundaries lie — it is the seed row set
 * from migration V70, reproduced so a response without boundaries shows the documented defaults instead
 * of a blank where PRD 04 requires a range. The popover tells the reader when it is showing them.
 */

import type { RiskBand } from '@/api/adapters/patients';

/**
 * `ad_biometric_risk_band` as migration V70 seeds it — the same three rows PRD 04 §3.1 and PRD 11 §7.2
 * print. Used only when the response carries no boundaries at all.
 */
export const R_BAND_SEED: RiskBand[] = [
	{ band: 'LOW', min: 0, max: 5.9 },
	{ band: 'MODERATE', min: 6, max: 14.4 },
	{ band: 'HIGH', min: 14.5, max: 26.8 },
];

export interface ResolvedRiskBands {
	/** Lowest band first, which is the order the popover lists them in. */
	bands: RiskBand[];
	/** The full R scale the popover's 범위 line prints: the lowest min and the highest max. */
	min: number;
	max: number;
	/** True when the server sent no boundaries and these are the V70 seed values. */
	isSeed: boolean;
}

/**
 * The boundaries to display, and whether they came from the server.
 *
 * The scale ends are read OFF the bands rather than kept as their own constants: 26.8 is only the seed
 * high maximum — V70 puts no upper cap on the column because the R-value is a running sum — so a
 * hospital that raises the top band must see its own ceiling in the 범위 line, not 26.8.
 */
export function resolveRiskBands(bands?: RiskBand[]): ResolvedRiskBands {
	const resolved = bands && bands.length > 0 ? bands : R_BAND_SEED;
	const sorted = [...resolved].sort((a, b) => a.min - b.min);
	return {
		bands: sorted,
		min: Math.min(...sorted.map((b) => b.min)),
		max: Math.max(...sorted.map((b) => b.max)),
		isSeed: !(bands && bands.length > 0),
	};
}

/**
 * A boundary as the popover writes it.
 *
 * The column is DECIMAL(4,1), so one decimal place is the full precision a boundary can carry and
 * rounding to it cannot lose a configured value — it only absorbs the float error a JSON round-trip can
 * introduce (a stored `14.4` arriving as `14.399999999999999` must not print as that). The trailing
 * `.0` is then dropped so a whole boundary reads `6`, matching the wording PRD 04 §3.1 and the copy this
 * replaces both use.
 */
export function formatBoundary(value: number): string {
	return String(Math.round(value * 10) / 10);
}
